import { useState, useEffect } from "react";
import { Trash2, Pencil, Plus, LogIn, Globe, Users } from "lucide-react";
import { Button, Modal, Table, PhoneInput } from "../../../components";
import axios from "axios";
import {
  SUPERADMIN_COMPANIES_URL,
  SUPERADMIN_UPDATE_COMPANY_URL,
  SUPERADMIN_DELETE_COMPANY_URL,
  PLATFORM_ACT_AS_URL,
} from "../../../features/UrlHelper";
import { setAuthTokenForSlug } from "../../../features/tenantStorage";
import CompanyDomainsModal from "./CompanyDomainsModal";
import CompanyUsersModal from "./CompanyUsersModal";

/**
 * Sprava prevadzok v platformovej sprave (presunute z admin panela).
 *
 * Oproti povodnej verzii: slug (adresa rozvozy.sk/<slug>), branding, platnost
 * a akcie Domeny / Pouzivatelia / Vstupit (act-as).
 */
const PRAZDNA_FIRMA = {
  name: "",
  slug: "",
  address: "",
  phone: "",
  email: "",
  google_maps_url: "",
  valid_to: "",
  display_name: "",
  logo_url: "",
  favicon_url: "",
  hero_video_url: "",
  hero_image_url: "",
  hero_title: "",
  hero_subtitle: "",
  primary_color: "",
  meta_description: "",
};

const BRANDING_POLIA = [
  { key: "display_name", label: "Zobrazovaný názov", placeholder: "Veterán Pub", maxLength: 100 },
  { key: "hero_title", label: "Nadpis na úvodnej stránke", placeholder: "Nový riadok = \\n", maxLength: 150 },
  { key: "hero_subtitle", label: "Podnadpis", maxLength: 300 },
  { key: "hero_video_url", label: "Video na pozadí (URL)", placeholder: "/Veteran.mp4", maxLength: 512 },
  { key: "hero_image_url", label: "Obrázok na pozadí (URL)", maxLength: 512 },
  { key: "logo_url", label: "Logo (URL)", placeholder: "/rozvozy_icon.png", maxLength: 512 },
  { key: "favicon_url", label: "Favicon (URL)", maxLength: 512 },
  { key: "primary_color", label: "Hlavná farba", placeholder: "#d6140a", maxLength: 7 }, // farba-z-dat (vzor hodnoty do DB)
  { key: "meta_description", label: "Popis pre vyhľadávače", maxLength: 300 },
];

const PHONE_PREFIXES = ["+421", "+420"];
const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");
const formatDatum = (d) => (d ? new Date(d).toLocaleDateString("sk-SK") : "—");
const jeExpirovana = (c) => c.valid_to && new Date(c.valid_to) <= new Date();

function CompaniesSection() {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyForm, setCompanyForm] = useState(PRAZDNA_FIRMA);

  const [companyPhoneDialCode, setCompanyPhoneDialCode] = useState("+421");
  const [companyPhoneLocal, setCompanyPhoneLocal] = useState("");

  const [domenyFirmy, setDomenyFirmy] = useState(null);
  const [pouzivateliaFirmy, setPouzivateliaFirmy] = useState(null);
  const [vstupuje, setVstupuje] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
    try {
      const response = await axios.get(SUPERADMIN_COMPANIES_URL);
      setCompanies(response.data);
    } catch (error) {
      console.error("Chyba pri načítaní firiem:", error);
    } finally {
      setLoadingCompanies(false);
    }
  }

  const pole = (key) => ({
    value: companyForm[key] ?? "",
    onChange: (e) => setCompanyForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  const openAddCompany = () => {
    setEditingCompany(null);
    setCompanyForm(PRAZDNA_FIRMA);
    setCompanyPhoneDialCode("+421");
    setCompanyPhoneLocal("");
    setCompanyModalOpen(true);
  };

  const openEditCompany = (company) => {
    setEditingCompany(company);
    const rawPhone = company.phone || "";
    const dialCode = PHONE_PREFIXES.find((p) => rawPhone.startsWith(p)) ?? "+421";
    const localNum = rawPhone.startsWith(dialCode) ? rawPhone.slice(dialCode.length) : rawPhone;
    setCompanyPhoneDialCode(dialCode);
    setCompanyPhoneLocal(localNum);
    const form = { ...PRAZDNA_FIRMA };
    for (const key of Object.keys(PRAZDNA_FIRMA)) form[key] = company[key] ?? "";
    form.valid_to = toDateInput(company.valid_to);
    // v DB je \n ako skutocny novy riadok -- vo formulari ho ukazeme ako \n
    form.hero_title = String(form.hero_title || "").replace(/\n/g, "\\n");
    setCompanyForm(form);
    setCompanyModalOpen(true);
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const payload = {
      ...companyForm,
      slug: companyForm.slug.trim().toLowerCase(),
      hero_title: companyForm.hero_title.replace(/\\n/g, "\n"),
    };
    // Pri zakladani prazdny slug = odvodi ho backend z nazvu
    if (!editingCompany && !payload.slug) delete payload.slug;
    try {
      if (editingCompany) {
        await axios.post(SUPERADMIN_UPDATE_COMPANY_URL(editingCompany.id), payload);
      } else {
        await axios.put(SUPERADMIN_COMPANIES_URL, payload);
      }
      setCompanyModalOpen(false);
      await loadCompanies();
    } catch (error) {
      console.error("Chyba pri ukladaní firmy:", error);
      alert(error?.response?.data?.message || "Nastala chyba pri ukladaní firmy.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteCompany = async (id) => {
    setIsDeleting(true);
    try {
      await axios.delete(SUPERADMIN_DELETE_COMPANY_URL(id));
      setCompanies((prev) => prev.filter((c) => c.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Chyba pri mazaní firmy:", error);
      alert(error?.response?.data?.message || "Nastala chyba pri mazaní firmy.");
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * "Pracovat ako" prevadzka: backend vyda 1 h token s actingAs. Ulozi sa
   * do kluca CIELOVEJ prevadzky (tenantStorage) a prejde sa na jej admin --
   * platformovy token tu ostava, "Ukoncit" v pruhu vrati spat do /sprava.
   */
  const vstup = async (company) => {
    setVstupuje(company.id);
    try {
      const { data } = await axios.post(PLATFORM_ACT_AS_URL, { companyId: company.id });
      setAuthTokenForSlug(data.companySlug, data.token);
      window.location.assign(`/${data.companySlug}/admin`);
    } catch (error) {
      alert(error?.response?.data?.message || "Do prevádzky sa nepodarilo vstúpiť.");
      setVstupuje(null);
    }
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Prevádzky</h2>
        <Button variant="primary" onClick={openAddCompany}>
          <Plus size={16} /> Pridať prevádzku
        </Button>
      </div>

      <div className="admin__table-wrapper">
        <Table
          columns={[
            { key: "id", label: "ID" },
            { key: "name", label: "Názov" },
            {
              key: "slug",
              label: "Adresa",
              render: (val) =>
                val ? (
                  <a href={`/${val}`} target="_blank" rel="noopener noreferrer">
                    /{val}
                  </a>
                ) : (
                  "—"
                ),
            },
            {
              key: "is_demo",
              label: "Typ",
              render: (val) => (val ? "Demo" : "Ostrá"),
            },
            {
              key: "valid_to",
              label: "Platnosť",
              render: (val, row) =>
                val ? (
                  <span className={jeExpirovana(row) ? "platform__expired" : undefined}>
                    {jeExpirovana(row) ? "Vypršala " : "do "}
                    {formatDatum(val)}
                  </span>
                ) : (
                  "Neobmedzená"
                ),
            },
            { key: "email", label: "Email" },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button
                    className="admin__icon-btn"
                    onClick={() => vstup(row)}
                    title="Pracovať ako táto prevádzka"
                    disabled={!row.slug || vstupuje === row.id}
                  >
                    <LogIn size={16} />
                  </button>
                  <button className="admin__icon-btn" onClick={() => setDomenyFirmy(row)} title="Vlastné domény">
                    <Globe size={16} />
                  </button>
                  <button className="admin__icon-btn" onClick={() => setPouzivateliaFirmy(row)} title="Používatelia">
                    <Users size={16} />
                  </button>
                  <button className="admin__icon-btn" onClick={() => openEditCompany(row)} title="Upraviť">
                    <Pencil size={16} />
                  </button>
                  <button
                    className="admin__icon-btn admin__icon-btn--danger"
                    onClick={() => setDeleteConfirm(row.id)}
                    title="Zmazať"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={companies}
          searchPlaceholder="Hľadať prevádzku..."
          emptyMessage={loadingCompanies ? "Načítavam..." : "Žiadne prevádzky"}
        />
      </div>

      {/* Add / Edit company modal */}
      <Modal
        isOpen={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        title={editingCompany ? "Upraviť prevádzku" : "Pridať prevádzku"}
      >
        <form className="admin__form" onSubmit={handleCompanySubmit}>
          <div className="admin__form-field">
            <label className="admin__form-label">Názov firmy</label>
            <input maxLength={50} className="admin__form-input" {...pole("name")} placeholder="Názov firmy" required />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Adresa v URL (slug)</label>
            <input
              maxLength={40}
              className="admin__form-input"
              {...pole("slug")}
              placeholder={editingCompany ? "" : "odvodí sa z názvu"}
              required={!!editingCompany}
            />
            <small className="platform__hint">
              rozvozy.sk/{companyForm.slug || "…"} — zmena slugu zmení adresu, ktorú zákazníci poznajú.
            </small>
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Adresa</label>
            <input maxLength={100} className="admin__form-input" {...pole("address")} placeholder="Ulica, Mesto" />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Telefón</label>
            <PhoneInput
              value={companyPhoneLocal}
              prefix={companyPhoneDialCode}
              onChange={(local) => {
                setCompanyPhoneLocal(local);
                setCompanyForm((prev) => ({ ...prev, phone: companyPhoneDialCode + local }));
              }}
              onPrefixChange={(dial) => {
                setCompanyPhoneDialCode(dial);
                setCompanyForm((prev) => ({ ...prev, phone: dial + companyPhoneLocal }));
              }}
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Email</label>
            <input maxLength={50} type="email" className="admin__form-input" {...pole("email")} placeholder="firma@example.com" />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Google Maps URL</label>
            <input maxLength={512} type="url" className="admin__form-input" {...pole("google_maps_url")} placeholder="https://maps.google.com/..." />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Platná do (prázdne = neobmedzene)</label>
            <input type="date" className="admin__form-input" {...pole("valid_to")} />
          </div>

          <details className="platform__branding">
            <summary>Vzhľad prevádzky (branding)</summary>
            {BRANDING_POLIA.map(({ key, label, placeholder, maxLength }) => (
              <div key={key} className="admin__form-field">
                <label className="admin__form-label">{label}</label>
                <input className="admin__form-input" maxLength={maxLength} placeholder={placeholder} {...pole(key)} />
              </div>
            ))}
          </details>

          <div className="admin__form-actions">
            <Button variant="ghost" type="button" onClick={() => setCompanyModalOpen(false)}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Ukladám..." : editingCompany ? "Uložiť zmeny" : "Pridať prevádzku"}
            </Button>
          </div>
        </form>
      </Modal>

      {domenyFirmy && <CompanyDomainsModal company={domenyFirmy} onClose={() => setDomenyFirmy(null)} />}
      {pouzivateliaFirmy && <CompanyUsersModal company={pouzivateliaFirmy} onClose={() => setPouzivateliaFirmy(null)} />}

      {/* Delete confirmation modal */}
      <Modal isOpen={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)} title="Potvrdiť zmazanie">
        <p>Naozaj chcete zmazať túto prevádzku? Jej adresa (slug) aj domény sa uvoľnia.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deleteCompany(deleteConfirm)}
            disabled={isDeleting}
            style={{ backgroundColor: "var(--c-error)", borderColor: "var(--c-error)" }}
          >
            {isDeleting ? "Mazanie..." : "Zmazať"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default CompaniesSection;
